import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import {
  MODAL_ACTIONS,
  MODAL_DESCRIPTION,
  MODAL_OVERLAY,
  MODAL_SURFACE,
  MODAL_TITLE,
} from "./modal-chrome";

/**
 * A modal in the house style, which the alert dialog shares. Always modal: the
 * card sits inside the overlay, and Radix draws no overlay for a non-modal one.
 *
 * Only the pieces a caller uses are exported; the rest of the shadcn dialog
 * surface can arrive when something needs it.
 */
const Dialog = (
  props: Omit<React.ComponentProps<typeof DialogPrimitive.Root>, "modal">,
) => <DialogPrimitive.Root {...props} />;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  ref?: React.RefObject<React.ComponentRef<
    typeof DialogPrimitive.Overlay
  > | null>;
}) => (
  <DialogPrimitive.Overlay
    ref={ref}
    // Named because the overlay exposes no role, and clicking it closes.
    data-testid="dialog-overlay"
    className={cn(MODAL_OVERLAY, className)}
    {...props}
  />
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = ({
  ref,
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  ref?: React.RefObject<React.ComponentRef<
    typeof DialogPrimitive.Content
  > | null>;
}) => (
  <DialogPortal>
    {/* The overlay holds the card and centres it: see modal-chrome. */}
    <DialogOverlay>
      <DialogPrimitive.Content
        ref={ref}
        // Stated as absent unless the caller names its description: Radix warns
        // on every open otherwise.
        aria-describedby={undefined}
        className={cn(MODAL_SURFACE, className)}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogOverlay>
  </DialogPortal>
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogTitle = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  ref?: React.RefObject<React.ComponentRef<
    typeof DialogPrimitive.Title
  > | null>;
}) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(MODAL_TITLE, className)}
    {...props}
  />
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
  ref?: React.RefObject<React.ComponentRef<
    typeof DialogPrimitive.Description
  > | null>;
}) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(MODAL_DESCRIPTION, className)}
    {...props}
  />
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

/** The dialog's actions, set at its foot and to the right, as the alert dialog sets its own. */
const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn(MODAL_ACTIONS, className)} {...props} />
);

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogClose,
  DialogTitle,
  DialogDescription,
  DialogFooter,
};
